import React, { createContext, useContext, useState, useCallback, useMemo, ReactNode } from 'react';
import { createTheme, Theme, ThemeProvider as MuiThemeProvider } from '@mui/material/styles';
import { CssBaseline } from '@mui/material';
import { ptBR } from '@mui/material/locale';

// ============================================================================
// Types
// ============================================================================

export interface BrandingColors {
  primaryColor: string;
  secondaryColor: string;
  accentColor: string;
  borderRadius: number;
}

interface ThemeContextType {
  isDarkMode: boolean;
  toggleDarkMode: () => void;
  branding: BrandingColors;
  updateBranding: (colors: Partial<BrandingColors>) => void;
  resetBranding: () => void;
}

// ============================================================================
// Constants
// ============================================================================

const DEFAULT_BRANDING: BrandingColors = {
  primaryColor: '#1B6A63', // Clinafy Green
  secondaryColor: '#2E8C80', // Deep Mint
  accentColor: '#27ab83', // Teal
  borderRadius: 8
};

const STORAGE_KEY = 'clinafy_dark_mode';
const BRANDING_STORAGE_KEY = 'clinafy_branding';

// ============================================================================
// Color Utilities
// ============================================================================

const lightenColor = (color: string, percent: number): string => {
  try {
    const num = parseInt(color.replace('#', ''), 16);
    const amt = Math.round(2.55 * percent);
    const R = (num >> 16) + amt;
    const G = ((num >> 8) & 0x00FF) + amt;
    const B = (num & 0x0000FF) + amt;
    return '#' + (
      0x1000000 +
      (R < 255 ? (R < 1 ? 0 : R) : 255) * 0x10000 +
      (G < 255 ? (G < 1 ? 0 : G) : 255) * 0x100 +
      (B < 255 ? (B < 1 ? 0 : B) : 255)
    ).toString(16).slice(1).toUpperCase();
  } catch {
    return color;
  }
};

const darkenColor = (color: string, percent: number): string => {
  try {
    const num = parseInt(color.replace('#', ''), 16);
    const amt = Math.round(2.55 * percent);
    const R = (num >> 16) - amt;
    const G = ((num >> 8) & 0x00FF) - amt;
    const B = (num & 0x0000FF) - amt;
    return '#' + (
      0x1000000 +
      (R > 0 ? R : 0) * 0x10000 +
      (G > 0 ? G : 0) * 0x100 +
      (B > 0 ? B : 0)
    ).toString(16).slice(1).toUpperCase();
  } catch {
    return color;
  }
};

const getContrastColor = (hexColor: string): string => {
  try {
    const r = parseInt(hexColor.substr(1, 2), 16);
    const g = parseInt(hexColor.substr(3, 2), 16);
    const b = parseInt(hexColor.substr(5, 2), 16);
    const brightness = (r * 299 + g * 587 + b * 114) / 1000;
    return brightness > 128 ? '#000000' : '#FFFFFF';
  } catch {
    return '#FFFFFF';
  }
};

// ============================================================================
// Context
// ============================================================================

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

// ============================================================================
// Theme Builder
// ============================================================================

const createAppTheme = (isDarkMode: boolean, branding: BrandingColors): Theme => {
  const { primaryColor, secondaryColor, accentColor, borderRadius } = branding;

  return createTheme({
    palette: {
      mode: isDarkMode ? 'dark' : 'light',
      primary: {
        main: primaryColor,
        light: lightenColor(primaryColor, 20),
        dark: darkenColor(primaryColor, 20),
        contrastText: getContrastColor(primaryColor)
      },
      secondary: {
        main: secondaryColor,
        light: lightenColor(secondaryColor, 20),
        dark: darkenColor(secondaryColor, 20),
        contrastText: getContrastColor(secondaryColor)
      },
      background: {
        default: isDarkMode ? '#121212' : '#E8F0EC',
        paper: isDarkMode ? '#1E1E1E' : '#FFFFFF'
      },
      error: {
        main: '#D76D6D'
      },
      warning: {
        main: '#DBA878'
      },
      success: {
        main: accentColor,
        light: lightenColor(accentColor, 20),
        dark: darkenColor(accentColor, 20)
      },
      info: {
        main: '#78A2BA'
      },
      text: {
        primary: isDarkMode ? '#FFFFFF' : 'rgba(0, 0, 0, 0.87)',
        secondary: isDarkMode ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.6)'
      },
      divider: isDarkMode ? 'rgba(255, 255, 255, 0.12)' : 'rgba(0, 0, 0, 0.12)'
    },
    typography: {
      fontFamily: [
        'Raleway',
        '"Helvetica Neue"',
        'Arial',
        'sans-serif'
      ].join(','),
      h4: { fontWeight: 700 },
      h5: { fontWeight: 600 },
      h6: { fontWeight: 600 },
      button: { fontWeight: 600 }
    },
    shape: {
      borderRadius: borderRadius
    },
    components: {
      MuiPaper: {
        styleOverrides: {
          root: {
            boxShadow: isDarkMode
              ? '0px 3px 6px rgba(0, 0, 0, 0.3)'
              : '0px 3px 6px rgba(0, 0, 0, 0.1)'
          }
        }
      },
      MuiButton: {
        styleOverrides: {
          root: {
            textTransform: 'none',
            fontWeight: 600
          }
        }
      },
      MuiAppBar: {
        styleOverrides: {
          root: {
            backgroundColor: primaryColor
          }
        }
      },
      MuiDrawer: {
        styleOverrides: {
          paper: {
            backgroundColor: isDarkMode ? '#1E1E1E' : '#FFFFFF'
          }
        }
      }
    }
  }, ptBR); // locale PT-BR: paginação ("Linhas por página", "de"), pickers, etc.
};

// ============================================================================
// Provider Component
// ============================================================================

interface ThemeContextProviderProps {
  children: ReactNode;
}

export const ThemeContextProvider: React.FC<ThemeContextProviderProps> = ({ children }) => {
  // Initialize dark mode from localStorage
  const [isDarkMode, setIsDarkMode] = useState<boolean>(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      return saved === 'true';
    } catch {
      return false;
    }
  });

  // Initialize branding from localStorage
  const [branding, setBranding] = useState<BrandingColors>(() => {
    try {
      const saved = localStorage.getItem(BRANDING_STORAGE_KEY);
      if (saved) {
        return { ...DEFAULT_BRANDING, ...JSON.parse(saved) };
      }
    } catch {
      // Ignore parse errors
    }
    return DEFAULT_BRANDING;
  });

  // Toggle dark mode
  const toggleDarkMode = useCallback(() => {
    setIsDarkMode(prev => {
      const newMode = !prev;
      try {
        localStorage.setItem(STORAGE_KEY, String(newMode));
      } catch (error) {
        console.error('Error saving theme preference:', error);
      }
      return newMode;
    });
  }, []);

  // Update branding colors
  const updateBranding = useCallback((colors: Partial<BrandingColors>) => {
    setBranding(prev => {
      const newBranding = { ...prev, ...colors };
      try {
        localStorage.setItem(BRANDING_STORAGE_KEY, JSON.stringify(newBranding));
        console.log('Branding updated:', newBranding);
      } catch (error) {
        console.error('Error saving branding:', error);
      }
      return newBranding;
    });
  }, []);

  // Reset branding to defaults
  const resetBranding = useCallback(() => {
    setBranding(DEFAULT_BRANDING);
    try {
      localStorage.removeItem(BRANDING_STORAGE_KEY);
    } catch (error) {
      console.error('Error clearing branding:', error);
    }
  }, []);

  // Memoized theme
  const theme = useMemo(() => createAppTheme(isDarkMode, branding), [isDarkMode, branding]);

  // Context value
  const contextValue = useMemo(() => ({
    isDarkMode,
    toggleDarkMode,
    branding,
    updateBranding,
    resetBranding
  }), [isDarkMode, toggleDarkMode, branding, updateBranding, resetBranding]);

  return (
    <ThemeContext.Provider value={contextValue}>
      <MuiThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </MuiThemeProvider>
    </ThemeContext.Provider>
  );
};

// ============================================================================
// Hook
// ============================================================================

export const useThemeMode = (): ThemeContextType => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useThemeMode must be used within ThemeContextProvider');
  }
  return context;
};

export default ThemeContextProvider;
